import { Bell, BellRing, Calculator, CheckCircle2, Coins, Headphones, Landmark, PlayCircle, Settings, Share2, ShieldCheck, Store, UsersRound, X, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { isReelCaptureMode } from "@/lib/reelCapture";
import { captureDeferredInstallPrompt, clearDeferredInstallPrompt, getDeferredInstallPrompt, type DeferredInstallPrompt } from "@/lib/pwaInstall";
import { getFirstPushOnboardingStorageKey } from "@/lib/pushOnboarding";
import "../components/NotificationOnboarding.css";

type HomeLandingProps = { onOpenApp: (label?: string) => void };

const tiles = [
  { key: "gold", title: "أسعار الذهب", icon: <Coins />, tone: "yellow" },
  { key: "energy", title: "الطاقة والمعادن", icon: <Zap />, tone: "blue" },
  { key: "exchange", title: "أسعار الصرف", icon: <Landmark />, tone: "green" },
  { key: "guide", title: "دليل الصاغة", icon: <Landmark />, tone: "teal" },
  { key: "calculations", title: "حسابات الذهب", icon: <Calculator />, tone: "purple" },
  { key: "support", title: "فريق الدعم", icon: <Headphones />, tone: "orange" },
  { key: "merchant", title: "دخول التاجر", icon: <Store />, tone: "blue" },
  { key: "watch", title: "شاهد", icon: <PlayCircle />, tone: "purple" },
];

const PUSH_CLIENT_KEY = "iraq-gold-notification-client";

function getAnalyticsClientKey() {
  const key = "iraq-gold-analytics-client";
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const created = typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(key, created);
  return created;
}

function getPushClientKey() {
  const existing = localStorage.getItem(PUSH_CLIENT_KEY);
  if (existing && /^[A-Za-z0-9_-]{16,96}$/.test(existing)) return existing;
  const random = globalThis.crypto?.randomUUID?.().replace(/-/g, "") ?? `${Date.now()}${Math.random().toString(36).slice(2)}`;
  const key = `iqg_${random}`.slice(0, 96);
  localStorage.setItem(PUSH_CLIENT_KEY, key);
  return key;
}

function getAnalyticsDeviceType(): "iphone" | "android" | "tablet" | "desktop" | "other" {
  const ua = navigator.userAgent.toLowerCase();
  if (/ipad|tablet/.test(ua) || (navigator.maxTouchPoints > 1 && /macintosh/.test(ua))) return "tablet";
  if (/iphone|ipod/.test(ua)) return "iphone";
  if (/android/.test(ua)) return "android";
  if (/windows|macintosh|linux/.test(ua)) return "desktop";
  return "other";
}

function toPushApplicationServerKey(value: string) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const raw = window.atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, character => character.charCodeAt(0));
}

function needsIosInstall() {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !("MSStream" in window);
  const standalone = window.matchMedia("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
  return ios && !standalone;
}

export default function HomeLanding({ onOpenApp }: HomeLandingProps) {
  const [clientKey] = useState(getAnalyticsClientKey);
  const [visits, setVisits] = useState<number | null>(null);
  const [pushPublicKey, setPushPublicKey] = useState("");
  const [showPushOnboarding, setShowPushOnboarding] = useState(false);
  const [pushOnboardingState, setPushOnboardingState] = useState<"default" | "loading" | "success" | "ios" | "failed">("default");
  const [installPrompt, setInstallPrompt] = useState<DeferredInstallPrompt | null>(() => getDeferredInstallPrompt());
  const [isStandalone, setIsStandalone] = useState(() => window.matchMedia("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone));
  const [installHelpOpen, setInstallHelpOpen] = useState(false);
  const [isAppleDevice] = useState(() => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
  const [isAndroidDevice] = useState(() => /Android/i.test(navigator.userAgent));

  useEffect(() => {
    void fetch("/api/public/visit-count", { cache: "no-store" }).then(response => response.ok ? response.json() : null).then(data => setVisits(typeof data?.count === "number" ? data.count : null)).catch(() => undefined);
    void fetch("/api/public/visit", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ clientKey, path: window.location.pathname, deviceType: getAnalyticsDeviceType() }), keepalive: true }).catch(() => undefined);
  }, [clientKey]);

  useEffect(() => {
    if (isReelCaptureMode(window.location.search)) return;
    const onboardingKey = getFirstPushOnboardingStorageKey(isStandalone);
    if (localStorage.getItem(onboardingKey) === "handled" || typeof Notification === "undefined" || Notification.permission !== "default") return;
    void fetch("/api/public/push-config", { cache: "no-store" }).then(response => response.ok ? response.json() : null).then(data => {
      if (typeof data?.publicKey !== "string" || !data.publicKey) return;
      setPushPublicKey(data.publicKey);
      setShowPushOnboarding(true);
    }).catch(() => undefined);
  }, [isStandalone]);

  useEffect(() => {
    const syncPrompt = () => setInstallPrompt(getDeferredInstallPrompt());
    const onBeforeInstall = (event: Event) => { captureDeferredInstallPrompt(event); syncPrompt(); };
    const onInstalled = () => { clearDeferredInstallPrompt(); setInstallPrompt(null); setIsStandalone(true); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("iraq-gold-install-available", syncPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onBeforeInstall); window.removeEventListener("iraq-gold-install-available", syncPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const openApp = (hash: string, label: string, eventType?: "settings" | "notifications") => {
    if (eventType === "notifications") sessionStorage.setItem("iraq-gold-open-notifications", "true");
    window.location.hash = hash;
    onOpenApp(label);
  };

  const shareApp = async () => {
    const share = { title: "ذهب العراق", text: "تابع أسعار الذهب في العراق مباشرة", url: window.location.origin };
    try {
      if (navigator.share) await navigator.share(share);
      else await navigator.clipboard?.writeText(share.url);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) console.warn("[Share] App sharing failed", error);
    }
  };

  const installAppFromHome = async () => {
    const recordInstallEvent = (eventType: "install_click" | "install_prompt_accepted" | "install_prompt_dismissed" | "install_unavailable") => void fetch("/api/public/visit", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ clientKey, path: window.location.pathname, eventType, deviceType: getAnalyticsDeviceType() }), keepalive: true }).catch(() => undefined);
    recordInstallEvent("install_click");
    const deferredPrompt = installPrompt ?? getDeferredInstallPrompt();
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      recordInstallEvent(result.outcome === "accepted" ? "install_prompt_accepted" : "install_prompt_dismissed");
      clearDeferredInstallPrompt();
      setInstallPrompt(null);
      return;
    }
    if (isAppleDevice) { setInstallHelpOpen(true); return; }
    if (isAndroidDevice) { recordInstallEvent("install_unavailable"); window.alert("سيظهر مربع التثبيت الرسمي عندما يكون Chrome جاهزاً للتثبيت. تأكد من فتح iraqgold.online من Chrome والاتصال بالإنترنت."); }
  };

  const dismissPushOnboarding = () => {
    localStorage.setItem(getFirstPushOnboardingStorageKey(isStandalone), "handled");
    setShowPushOnboarding(false);
  };

  const enablePushFromOnboarding = async () => {
    if (needsIosInstall()) { setPushOnboardingState("ios"); return; }
    if (!pushPublicKey || !("serviceWorker" in navigator) || !("PushManager" in window)) { setPushOnboardingState("failed"); return; }
    setPushOnboardingState("loading");
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") { dismissPushOnboarding(); return; }
      const registration = await navigator.serviceWorker.register("/notification-sw.js", { scope: "/" });
      await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toPushApplicationServerKey(pushPublicKey) });
      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) throw new Error("invalid-subscription");
      const response = await fetch("/api/public/push-subscription", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ clientKey: getPushClientKey(), endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth }) });
      if (!response.ok) throw new Error("subscription-save-failed");
      localStorage.setItem(getFirstPushOnboardingStorageKey(isStandalone), "handled");
      setPushOnboardingState("success");
    } catch { setPushOnboardingState("failed"); }
  };

  return <div className="market-app home-view" dir="rtl">
    <header className="market-header home-header">
      <h1>ذهب العراق</h1>
      <div className="header-icons">
        <button aria-label="الإشعارات" onClick={() => openApp("", "الإشعارات", "notifications")}><Bell size={20} /></button>
        <button aria-label="مشاركة التطبيق" onClick={() => void shareApp()}><Share2 size={20} /></button>
        <button aria-label="الإعدادات" onClick={() => openApp("settings", "الإعدادات", "settings")}><Settings size={20} /></button>
      </div>
    </header>
    {showPushOnboarding && <div className="first-push-onboarding-backdrop" role="presentation"><section className="first-push-onboarding" role="dialog" aria-modal="true" aria-labelledby="first-push-onboarding-title"><button type="button" className="first-push-onboarding-close" onClick={dismissPushOnboarding} aria-label="ليس الآن"><X size={18} /></button><div className="first-push-onboarding-mark" aria-hidden="true"><BellRing size={31} /></div>{pushOnboardingState === "ios" ? <><span className="first-push-onboarding-eyebrow">لأجهزة iPhone</span><h2 id="first-push-onboarding-title">ثبّت ذهب العراق أولاً</h2><p>اضغط زر المشاركة في Safari ثم «إضافة إلى الشاشة الرئيسية». بعد فتح التطبيق من الأيقونة تستطيع تفعيل التنبيهات مباشرة.</p><button type="button" className="first-push-onboarding-primary" onClick={dismissPushOnboarding}>فهمت</button></> : pushOnboardingState === "success" ? <><span className="first-push-onboarding-eyebrow">تم التفعيل بنجاح</span><h2 id="first-push-onboarding-title">أصبحت التنبيهات جاهزة</h2><p>ستصلك تحديثات أسعار الذهب المهمة حتى عندما لا يكون التطبيق مفتوحاً.</p><button type="button" className="first-push-onboarding-primary" onClick={() => setShowPushOnboarding(false)}><CheckCircle2 size={18} /> ممتاز</button></> : <><span className="first-push-onboarding-eyebrow">تحديثات أسعارك أولاً بأول</span><h2 id="first-push-onboarding-title">فعّل تنبيهات ذهب العراق</h2><p>{pushOnboardingState === "failed" ? "تعذر تجهيز الإشعارات الآن. يمكنك إعادة المحاولة أو تفعيلها لاحقاً من زر الجرس." : "استلم تحديثات الأونصة وعيار 21 والمثقال مباشرة عند تغيّر الأسعار المهمة."}</p><div className="first-push-onboarding-benefits"><span><ShieldCheck size={16} /> تنبيهات سعرية مهمة فقط</span><span><CheckCircle2 size={16} /> إيقافها متاح في أي وقت</span></div><button type="button" className="first-push-onboarding-primary" autoFocus onClick={() => void enablePushFromOnboarding()} disabled={pushOnboardingState === "loading"} aria-busy={pushOnboardingState === "loading"}>{pushOnboardingState === "loading" ? "جارٍ تجهيز الإذن…" : pushOnboardingState === "failed" ? "إعادة المحاولة" : "السماح بالإشعارات"}<Bell size={18} /></button><button type="button" className="first-push-onboarding-later" onClick={dismissPushOnboarding}>ليس الآن</button></>}</section></div>}
    <main className="home-main">
      <div className="home-subtitle">الخدمات والأسواق</div>
      <section className="tile-grid">{tiles.map(tile => <button key={tile.key} className="service-tile tile-active" onClick={() => openApp(tile.key === "calculations" ? "calc" : tile.key === "guide" ? "directory" : tile.key, tile.title)}><span className={`tile-icon ${tile.tone}`}>{tile.icon}</span><strong>{tile.title}</strong></button>)}</section>
      {!isStandalone && <section className="home-install-card" aria-label="تثبيت التطبيق"><div><span>ذهب العراق على هاتفك</span><strong>{isAppleDevice ? "ثبّت التطبيق عبر Safari" : isAndroidDevice ? "ثبّت التطبيق الآن" : "تثبيت التطبيق"}</strong><small>{isAppleDevice ? "أضفه إلى الشاشة الرئيسية للوصول السريع." : installPrompt ? "افتح مربع التثبيت الرسمي مباشرة." : "وصول سريع من الشاشة الرئيسية."}</small></div><button type="button" onClick={() => void installAppFromHome()}>{isAppleDevice ? "عرض الخطوات" : "تثبيت الآن"}<b aria-hidden="true">↓</b></button></section>}
      {installHelpOpen && <section className="home-install-hint" role="note"><strong>تثبيت ذهب العراق على iPhone</strong><span>اضغط زر المشاركة في Safari ثم اختر «إضافة إلى الشاشة الرئيسية» وبعدها «إضافة».</span><button type="button" aria-label="إخفاء تعليمات التثبيت" onClick={() => setInstallHelpOpen(false)}>×</button></section>}
      <section className="social-card"><h2>للتواصل مع ذهب العراق</h2><div><a href="https://wa.me/9647750336298" target="_blank" rel="noreferrer">واتساب</a><a href="https://instagram.com/u1fv" target="_blank" rel="noreferrer">إنستغرام</a></div></section>
      <section className="users-card"><UsersRound size={30} /><span>عدد الزيارات</span><b>{visits === null ? "—" : visits.toLocaleString("en-US")}</b></section>
      <footer>جميع الحقوق محفوظة © لطيف الصائغ · <a href="https://instagram.com/u1fv">@u1fv</a></footer>
    </main>
  </div>;
}
